import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { questionnaireService, testService } from '../services/api';
import { toast } from 'sonner';
import { Search, FileText, PlusCircle, Star, ExternalLink } from 'lucide-react';

export const LancerTestExternePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const nom = (searchParams.get('nom') || '').toUpperCase();
  const prenom = searchParams.get('prenom') || '';
  const boondId = searchParams.get('boond_id') || '';

  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async () => {
    try {
      const response = await questionnaireService.getAll({ page: 1, page_size: 100 });
      const data = response.data;
      setQuestionnaires(data.items || data);
    } catch (error) {
      toast.error('Erreur lors du chargement des questionnaires');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuestionnaire = async (questionnaireId) => {
    if (!nom || !prenom) {
      toast.error('Nom et prénom du candidat requis');
      return;
    }
    setCreating(true);
    try {
      const response = await testService.create(questionnaireId, nom, prenom);
      toast.success('Test créé — démarrage...');
      navigate(`/lancer-test/${questionnaireId}`, {
        state: { testId: response.data.id, autoStart: true }
      });
    } catch (error) {
      toast.error('Erreur lors de la création du test');
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateNew = () => {
    const params = new URLSearchParams();
    if (nom) params.set('nom', nom);
    if (prenom) params.set('prenom', prenom);
    if (boondId) params.set('boond_id', boondId);
    navigate(`/generer?${params.toString()}`);
  };

  const filtered = questionnaires.filter(q => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (q.fonction || '').toLowerCase().includes(s) ||
      (q.client || '').toLowerCase().includes(s) ||
      (q.ref_ao || '').toLowerCase().includes(s) ||
      (q.niveau || '').toLowerCase().includes(s) ||
      (q.tags || []).some(t => t.toLowerCase().includes(s))
    );
  });

  return (
    <Layout title="Lancer un test technique" breadcrumbs={['Accueil', 'Test externe']}>
      <div className="max-w-3xl">
        {/* Candidate Info Banner */}
        <div className="eva-card mb-6" data-testid="external-candidate-info">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Candidat</p>
              <p className="text-xl font-bold text-white">
                {prenom} {nom}
              </p>
              {boondId && (
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <ExternalLink size={12} />
                  Boond Manager ID : {boondId}
                </p>
              )}
            </div>
            {(!nom || !prenom) && (
              <div className="bg-[#EF4444]/10 text-[#EF4444] text-xs font-medium px-3 py-1.5 rounded-lg">
                Nom/Prénom manquant dans l'URL
              </div>
            )}
          </div>
        </div>

        {/* Option: Generate New */}
        <div
          onClick={handleGenerateNew}
          className="eva-card mb-6 cursor-pointer hover:shadow-md transition-all border-2 border-dashed border-white/10 hover:border-[#0B132B]"
          data-testid="generate-new-questionnaire-button"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
              <PlusCircle size={24} className="text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-semibold text-white">Générer un nouveau questionnaire</h3>
              <p className="text-sm text-slate-400">
                Créer un questionnaire sur mesure via IA, puis lancer le test pour {prenom || '...'} {nom || '...'}
              </p>
            </div>
          </div>
        </div>

        {/* Existing Questionnaires */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-3">
            Ou choisir un questionnaire existant
          </h3>
          <div className="relative mb-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="eva-input pl-10"
              placeholder="Rechercher par poste, client, réf. AO, tag..."
              data-testid="search-questionnaire-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-slate-400">Chargement...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 border border-white/10 rounded-lg">
            <FileText size={32} className="text-[#E9ECEF] mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              {search ? 'Aucun questionnaire trouvé pour cette recherche' : 'Aucun questionnaire disponible'}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="questionnaire-list">
            {filtered.map((q) => (
              <div
                key={q.id}
                className="eva-card cursor-pointer hover:shadow-md transition-all group"
                onClick={() => !creating && handleSelectQuestionnaire(q.id)}
                data-testid={`select-questionnaire-${q.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-white truncate">{q.fonction}</h4>
                      <span className="text-xs px-2 py-0.5 bg-white/5 text-slate-400 rounded shrink-0">
                        {q.niveau}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      {q.questions?.length || 0} questions
                      {q.client && ` — ${q.client}`}
                      {q.ref_ao && ` — AO : ${q.ref_ao}`}
                    </p>
                    {q.tags && q.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {q.tags.slice(0, 4).map((tag, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-white/5 text-[10px] text-slate-400 rounded">
                            {tag}
                          </span>
                        ))}
                        {q.tags.length > 4 && (
                          <span className="text-[10px] text-slate-400">+{q.tags.length - 4}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-4 shrink-0">
                    <span className="text-xs font-medium text-white bg-[#0B132B] px-3 py-1.5 rounded-lg">
                      Lancer le test
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};
